import React from 'react';

import { Flex } from 'antd-v5';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { MdInfo } from 'react-icons/md';
import { StatusTag, Text } from 'scouting-ui';
import { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';

import AdvancementAuditTooltip, {
  formatAuditDate,
  hasAuditInfo,
} from '@shared/components/AdvancementAudit/AdvancementAuditTooltip';

import RequirementControls from './RequirementControls';
import styles from './RequirementsRenderer.module.scss';
import { useRequirementsContext } from './context';
import type { RequirementNode } from './types';
import { resolveRequirementStatus } from './utils';

/** Strips the API's markup for a header line, where HTML would be noise */
export function toPlainText(html: string): string {
  return DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: [] }).trim();
}

/**
 * Completion date, status pill and audit data, shown once a requirement is
 * past not-started. All three sit inside the tooltip trigger so hovering any of
 * them opens the sign-off history
 */
export function RequirementStatus(props: { requirement: RequirementNode }) {
  const { requirement } = props;
  const tag = resolveRequirementStatus(requirement);

  if (tag === StatusProp.NOT_STARTED) return null;

  // a started requirement has a status but no date yet
  const completedOn = formatAuditDate(requirement.dateCompleted);

  return (
    <AdvancementAuditTooltip advancement={requirement}>
      <span className={styles.status}>
        {completedOn && (
          <Text font="body-caption" className={styles.completedOn}>
            {completedOn}
          </Text>
        )}
        <StatusTag status={tag} />
        {hasAuditInfo(requirement) && <MdInfo className={styles.auditIcon} />}
      </span>
    </AdvancementAuditTooltip>
  );
}

interface RequirementRowProps {
  requirement: RequirementNode;
  /** The top-level ancestor; the save payload needs it for the parent cascade */
  root: RequirementNode;
  /** 0 for the requirement that owns the collapse panel */
  depth: number;
}

/**
 * One requirement's body, recursing into its children. The panel header carries
 * the top-level requirement's own number and status, so depth 0 renders neither
 */
export default function RequirementRow(props: RequirementRowProps) {
  const { requirement, root, depth } = props;

  const { resolvePolicy, overrideContent, extraContent, onSaveRequirement } =
    useRequirementsContext();

  const policy = resolvePolicy(requirement);
  const override = overrideContent?.(requirement.id, requirement);
  const children = requirement.requirements ?? [];

  return (
    <div
      className={cn(styles.row, { [styles.nested]: depth > 0 })}
      data-requirement-id={requirement.id}
    >
      {depth > 0 && (
        <Flex align="center" gap="small" className={styles.rowHeader}>
          {requirement.listNumber && (
            <Text font="body-body-3" strong className={styles.listNumber}>
              {requirement.listNumber}
            </Text>
          )}
          <RequirementStatus requirement={requirement} />
        </Flex>
      )}

      {override ?? (
        <div
          className={styles.body}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(requirement.name ?? ''),
          }}
        />
      )}

      {!policy.readOnly && (
        <RequirementControls
          requirement={requirement}
          policy={policy}
          onSave={(id, values) => onSaveRequirement?.(id, values, root)}
        />
      )}

      {extraContent?.(requirement.id, requirement)}

      {children.map(child => (
        <RequirementRow
          key={child.id}
          requirement={child}
          root={root}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}
